import React, { useState } from 'react';
import ListingCard from './ListingCard';
import CardShelf from './CardShelf';
import GradeFilter, { filterByGrade } from './GradeFilter';
import './ListingsGrid.css';

// Year a listing belongs to: promo release date, else its set's release date,
// else the cert year.
const listingYear = (l) => {
    const promoDate = l.cert?.promo?.release_date || l.cert?.card?.promo?.release_date;
    if (promoDate) return new Date(promoDate).getFullYear();
    const s = l.cert?.card?.set;
    const rd = s?.releaseDate || s?.release_date;
    if (rd) return new Date(rd).getFullYear();
    const y = Number(l.cert?.year);
    return Number.isFinite(y) && y > 0 ? y : null;
};

// Break listings into subheader sections like the old site: promo cards grouped
// by promo name, everything else by rarity. Each group carries a year range.
const groupListings = (listings) => {
    const groups = {};
    listings.forEach((l) => {
        const promoName = l.cert?.promo?.display_name || l.cert?.card?.promo?.display_name;
        const key = promoName || l.cert?.card?.rarity || 'Other';
        if (!groups[key]) groups[key] = { key, name: key, listings: [] };
        groups[key].listings.push(l);
    });
    return Object.values(groups)
        .map((g) => {
            const years = g.listings.map(listingYear).filter((y) => y != null);
            const min = years.length ? Math.min(...years) : null;
            const max = years.length ? Math.max(...years) : null;
            g.yearLabel = min == null ? '' : (min === max ? `${min}` : `${min}–${max}`);
            g.sortYear = max == null ? -Infinity : max;
            return g;
        })
        .sort((a, b) => (b.sortYear - a.sortYear) || a.name.localeCompare(b.name));
};

const Grid = ({ listings }) => (
    <div className="gh-grid">
        {listings.map((l) => (
            <ListingCard key={l.id || l.listing_id || l.cert?.cert_number} listing={l} />
        ))}
    </div>
);

// A rarity/promo subheader section with its own PSA grade filter (like each
// homepage shelf and the old per-section filters).
const GradedGroup = ({ group }) => {
    const [grades, setGrades] = useState([]);
    const toggle = (g) =>
        setGrades((prev) => (prev.includes(g) ? prev.filter((x) => x !== g) : [...prev, g]));
    const filtered = filterByGrade(group.listings, grades);

    return (
        <div className="gh-grid-group">
            <div className="gh-grid-grouphead">
                <h2 className="gh-grid-grouptitle">
                    {group.name}
                    {group.yearLabel && <span className="gh-grid-groupyear">{group.yearLabel}</span>}
                </h2>
                <GradeFilter selected={grades} onToggle={toggle} />
            </div>
            {filtered.length === 0 ? (
                <p className="gh-grid-msg">No cards match this grade.</p>
            ) : (
                <Grid listings={filtered} />
            )}
        </div>
    );
};

// Listings pages show each rarity/promo group as a left-right scrolling shelf.
// Set to false to go back to the wrapping grid (GradedGroup above).
const USE_SHELVES = true;

// Listings page body. Cards are ALWAYS split into rarity/promo subheader
// sections, each with its own grade filter — there is never a whole-page grade
// filter. Each section is a horizontal shelf (USE_SHELVES) or the old grid.
// While loading, or when there are no listings, it shows blank space instead of
// a "no cards" message, and hides the count subtitle (it would read "0 cards").
// `error` is the only message it shows.
const ListingsGrid = ({ title, subtitle, listings = [], loading, error }) => {
    const groups = groupListings(listings);
    const hasListings = !loading && listings.length > 0;

    return (
        <section className="gh-grid-section">
            <div className="gh-grid-head">
                <h1 className="gh-grid-title">
                    {title}
                    {hasListings && subtitle && <span className="gh-grid-sub">{subtitle}</span>}
                </h1>
            </div>

            {error ? (
                <p className="gh-grid-msg">{error}</p>
            ) : hasListings ? (
                groups.map((g) => (USE_SHELVES ? (
                    <CardShelf key={g.key} title={g.name} meta={g.yearLabel} listings={g.listings} limit={Infinity} />
                ) : (
                    <GradedGroup key={g.key} group={g} />
                )))
            ) : (
                <div className="gh-grid-placeholder" aria-hidden="true" />
            )}
        </section>
    );
};

export default ListingsGrid;
